/**
 * Lightweight analytics service.
 *
 * All product events flow through `trackEvent()`.  In production the call is
 * forwarded to whatever provider is wired up (GA4 / Mixpanel / Segment / etc.).
 * In development it logs to the console so instrumentation can be verified
 * without a third-party account.
 *
 * Every event automatically inherits the "common dimensions" listed below so
 * downstream dashboards can slice by state, template, and custody ratio
 * without each call-site having to remember them.
 */

// ── Common dimensions (set once, attached to every event) ────────────────────

interface CommonDimensions {
  user_state: string | null;
  template_id: string | null;
  custody_ratio: string | null;   // e.g. "50/50", "60/40", "79/21"
  user_plan: string | null;
  user_id: string | null;
}

let _common: CommonDimensions = {
  user_state: null,
  template_id: null,
  custody_ratio: null,
  user_plan: null,
  user_id: null,
};

/** Merge partial updates into the common dimensions. */
export const setAnalyticsContext = (ctx: Partial<CommonDimensions>): void => {
  _common = { ..._common, ...ctx };
};

/** Read current context (useful for tests). */
export const getAnalyticsContext = (): Readonly<CommonDimensions> => _common;

/** Reset context (useful for sign-out). */
export const resetAnalyticsContext = (): void => {
  _common = { user_state: null, template_id: null, custody_ratio: null, user_plan: null, user_id: null };
};

// ── Core tracking ────────────────────────────────────────────────────────────

export type AnalyticsEvent =
  // Navigation / top-of-funnel
  | 'page_viewed'
  | 'landing_cta_clicked'
  | 'pricing_viewed'
  | 'auth_prompted'
  // Funnel: template → completion → export → conversion
  | 'template_selected'
  | 'template_picker_opened'
  | 'wizard_started'
  | 'manual_schedule_started'
  | 'schedule_block_added'
  | 'schedule_block_updated'
  | 'schedule_block_moved'
  | 'save_schedule_started'
  | 'readiness_checklist_viewed'
  | 'readiness_action_clicked'
  | 'plan_details_panel_viewed'
  | 'plan_details_updated'
  | 'template_review_prompt_viewed'
  | 'template_review_action_clicked'
  | 'wizard_schedule_applied'
  | 'schedule_completed'
  | 'calendar_exported'
  | 'court_packet_exported'
  | 'evidence_report_exported'
  | 'attorney_pdf_exported'
  // Feature unlock / upsell
  | 'support_calculator_viewed'
  | 'financial_calculator_viewed'
  | 'custody_analysis_viewed'
  | 'court_packet_viewed'
  | 'evidence_report_viewed'
  | 'attorney_share_viewed'
  // Conversion
  | 'checkout_started'
  | 'purchase_completed'
  // Engagement
  | 'schedule_saved'
  | 'calendar_unlocked'
  | 'day_override_set'
  | 'day_override_reset';

export interface EventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Fire an analytics event.
 *
 * Common dimensions are merged automatically; call-site properties win on
 * conflict so they can override when needed (e.g. a one-off state override).
 */
export const trackEvent = (event: AnalyticsEvent, properties: EventProperties = {}): void => {
  const payload: Record<string, unknown> = {
    ..._common,
    ...properties,
    event,
    timestamp: new Date().toISOString(),
  };

  // ── Provider forwarding ──────────────────────────────────────────────────
  // GA4 (gtag.js)
  if (typeof window !== 'undefined' && typeof (window as unknown as Record<string, unknown>).gtag === 'function') {
    ((window as unknown as Record<string, unknown>).gtag as (...args: unknown[]) => void)('event', event, payload);
  }

  // Generic dataLayer push (GTM-compatible)
  if (typeof window !== 'undefined' && Array.isArray((window as unknown as Record<string, unknown>).dataLayer)) {
    ((window as unknown as Record<string, unknown>).dataLayer as Record<string, unknown>[]).push({ event, ...payload });
  }

  // Dev logging
  if (import.meta.env.DEV) {
     
    console.log(
      `%c[analytics] %c${event}`,
      'color:#6366f1;font-weight:bold',
      'color:#10b981;font-weight:bold',
      payload,
    );
  }
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Compute a human-readable custody ratio string from a percentage.
 * e.g.  50 → "50/50",  57.1 → "57/43",  78.6 → "79/21"
 */
export const toCustodyRatio = (parentAPercentage: number): string => {
  const a = Math.round(parentAPercentage);
  return `${a}/${100 - a}`;
};
